import {Component, HostListener, OnInit} from "@angular/core";
import * as dayjs from 'dayjs';
import {PaymentControllerService, Staff, TableMerchant, UserControllerService} from "../../@services/gen";
import {AuthService} from '../../@services/auth.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TdDialogService} from '@covalent/core/dialogs';
import {NotificationService} from '../../@services/notification.service';
import {ActivatedRoute, Router} from '@angular/router';
import PaymentStatusEnum = Payment.PaymentStatusEnum;
import {isObjEmpty} from '../../@services/utils';
import {LocalstorageService} from '../../@services/localstorage.service';
import {Payment} from "../../@services/gen/model/payment";
import {UserShiftService} from "../../@services/user-shift.service";
import {FeatureService} from "../../@services/feature.service";
import {PaymentMethodEditDialogComponent} from "../../@components/payment-method-edit-dialog/payment-method-edit-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {ManagerAuthService} from "../../@services/manager-auth.service";

type PaymentVoucherCompat = Payment & {
  voucherAmount?: number;
};

@Component({
  selector: 'app-payment-list',
  templateUrl: './payment-list.component.html',
  styleUrls: ['./payment-list.component.scss'],
})
export class PaymentListComponent implements OnInit {
  startDate: string = dayjs(new Date()).format('YYYY-MM-DD HH:mm');
  endDate: string = dayjs(new Date()).format('YYYY-MM-DD HH:mm');
  payments: Payment[] = [];
  filteredPayments: Payment[] = [];
  search: string = '';
  title: string = 'Payment History';
  paidChecked: boolean = true;
  voidChecked: boolean = true;
  totalPaidAmount: number = 0;
  totalNetAmount: number = 0;
  totalChangeAmount: number = 0;
  tableId: number = 0;
  tables: TableMerchant[] = [];
  txStatus: string = 'PAID';
  txStatusEnum: PaymentStatusEnum;
  datePreset: string = 'today';
  isMobile: boolean = false;
  voidUser;
  voidRemark;

  constructor(
    private paymentService: PaymentControllerService,
    private customAuthService: AuthService,
    private userService: UserControllerService,
    private router: Router,
    private route: ActivatedRoute,
    private sb: MatSnackBar,
    private ns: NotificationService,
    private ls: LocalstorageService,
    private ds: TdDialogService,
    private userShiftService: UserShiftService,
    private featureService: FeatureService,
    private dialog: MatDialog,
    private managerAuthService: ManagerAuthService
  ) {
    this.checkIsMobile();
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((qp) => {
      if (isObjEmpty(qp)) {

        let userShift = this.userShiftService.getUserShift();

        if(this.userShiftService.getUserShift()){
          this.updateFilter({
            datePreset: 'session',
            startDate: dayjs(userShift.start).toDate(),
            endDate: dayjs().add(1, 'hour').toDate(), // Add 1 hour to the current time
            txStatus: 'PAID',
          });
        }

        return;
      }

      this.startDate = qp['startDate'];
      this.endDate = qp['endDate'];
      this.txStatus = qp['txStatus'];
      this.txStatusEnum = this.convertTxStatusToEnum(this.txStatus);
      this.datePreset = qp['datePreset'];

      this.load();
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.checkIsMobile();
  }

  private checkIsMobile() {
    this.isMobile = window.innerWidth < 768; // You can adjust the threshold as needed
  }

  load() {
    let dialogRef = this.ns.showNotification({
      message: 'Loading payments...',
      icon: 'fa fa-sync',
      manualClose: true,
    });

    this.paymentService
      .getPagedPaymentPost({
        startDate: dayjs(this.startDate).toString(),
        endDate: dayjs(this.endDate).endOf('day').toString(),
        merchantId: this.ls.getMerchant().id,
        tableId: this.tableId,
        search: this.search,
        paymentStatus: this.txStatusEnum,
        pageRequest: {
          orders: [{direction: 'DESC', property: 'creationTime'}],
        },
      })
      .subscribe((res) => {
        dialogRef.close();

        if (res) {
          this.payments = res.content;
          this.filteredPayments = [...this.payments];
          this.populateTableList();
          this.filterByStatus();
          this.calculateTotal();
        }
      });
  }

  private calculateTotal() {
    this.totalPaidAmount = this.filteredPayments.reduce((a, b) => a + b.paidAmount, 0);
    this.totalNetAmount = this.filteredPayments.reduce((a, b) => a + b.netAmount, 0);
    this.totalChangeAmount = this.filteredPayments.reduce((a, b) => a + b.changeAmount, 0);
  }

  private populateTableList() {
    let tableMerchants = this.filteredPayments.map((p) => p.tableMerchant);
    for (let tm of tableMerchants) {
      if (this.tables.findIndex((x) => x.id === tm.id) == -1) {
        this.tables.push(tm);
      }
    }
    this.tables.sort((a, b) => a.name.localeCompare(b.name));
  }

  voidPayment(payment: Payment) {
    if (this.featureService.isFeatureEnabled('voidPermission')) {
      this.checkVoidPermission(payment);
    } else {
      this.ds
        .openConfirm({
          title: 'Void Payment',
          message: 'Are you sure to void this payment? This action cannot be undone',
        })
        .afterClosed()
        .subscribe((confirmed) => {
          if (confirmed) {
            this.voidProcess(payment)
          }
        });
    }
  }

  voidProcess(payment: Payment){
    if(this.voidUser){
      payment.voidUser = this.voidUser;
    }
    if(this.voidRemark){
      payment.voidRemark = this.voidRemark;
    }

    this.paymentService
      .voidPaymentPost({
        payment: payment as any,
      })
      .subscribe((res) => {
        this.ns.showNotification({message: 'Payment has been voided'});
        this.load();
      });
  }

  private checkVoidPermission(payment: Payment) {
    this.managerAuthService.requireManagerAuth({ requireRemark: true }).subscribe(result => {
      if (result && result.success) {
        this.voidUser = result.username;
        this.voidRemark = result.remark;
        this.voidProcess(payment);
      }
    });
  }

  editPaymentMethod(payment: Payment) {
    // Safety check: Block editing for Staff Meal payments
    if (payment.staffMealAmount > 0 || (payment.paymentMethod && payment.paymentMethod.name.toLowerCase().includes('staff meal'))) {
      this.ns.showNotification({
        message: 'Staff Meal payments cannot be edited.',
        icon: 'fa fa-ban'
      });
      return;
    }
    // Safety check: Block editing for Voucher payments
    const voucherAmount = (payment as PaymentVoucherCompat).voucherAmount || 0;
    if (voucherAmount > 0) {
      this.ns.showNotification({
        message: 'Voucher payments cannot be edited.',
        icon: 'fa fa-ban'
      });
      return;
    }

    // Check if payment belongs to a closed shift
    let userShift = this.userShiftService.getUserShift();
    let isClosedShift = userShift && userShift.end; // Shift is closed if it has an end time

    if (isClosedShift) {
      // Show confirmation dialog for closed shift edits
      this.ds.openConfirm({
        title: 'Edit Closed Shift Payment',
        message: 'This payment belongs to a closed shift. Editing will automatically recalculate the closing report and notify managers. Continue?',
        acceptButton: 'Yes, Edit',
        cancelButton: 'Cancel'
      }).afterClosed().subscribe(confirmed => {
        if (confirmed) {
          this.proceedWithEdit(payment);
        }
      });
    } else {
      // Ongoing shift - proceed directly
      this.proceedWithEdit(payment);
    }
  }

  private proceedWithEdit(payment: Payment) {
    this.managerAuthService.requireManagerAuth().subscribe(authResult => {
      if (authResult && authResult.success) {
        this.openPaymentMethodDialog(payment, authResult.username, '');
      }
    });
  }

  openPaymentMethodDialog(payment: Payment, managerName: string, remark: string) {
    const dialogRef = this.dialog.open(PaymentMethodEditDialogComponent, {
      width: this.isMobile ? '90vw' : '500px',
      data: {
        payment: payment,
        managerName: managerName,
        remark: remark
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.process === 'save') {
        // 4. Call API to update payment method
        this.updatePaymentMethod(payment, result.newPaymentMethodId, result.managerName, result.remark, result.referenceNo);
      }
    });
  }

  updatePaymentMethod(payment: Payment, newPaymentMethodId: number, updatedBy: string, remark: string, referenceNo?: string) {
    let dialogRef = this.ns.showNotification({
      message: 'Updating payment method...',
      icon: 'fa fa-sync',
      manualClose: true,
    });

    const command = {
      paymentId: payment.id,
      newPaymentMethodId: newPaymentMethodId,
      updatedBy: updatedBy,
      remark: remark,
      referenceNo: referenceNo
    };

    // Assuming we added updatePaymentMethodPost to PaymentControllerService
    (this.paymentService as any).updatePaymentMethodPost(command).subscribe({
      next: (res) => {
        dialogRef.close();
        this.ns.showNotification({
          message: 'Payment method updated successfully',
          icon: 'fa fa-check-circle'
        });

        // Add small delay to ensure backend transaction commits before reload
        // This prevents race conditions where UI refreshes before DB writes complete
        setTimeout(() => {
          this.load(); // Reload the list
        }, 500); // 500ms delay
      },
      error: (err) => {
        dialogRef.close();
        console.error(err);
        this.ns.showNotification({
          message: 'Failed to update payment method: ' + (err.error?.message || err.message),
          icon: 'fa fa-times-circle'
        });
      }
    });
  }

  filterByStatus() {
    if (this.voidChecked && this.paidChecked) {
      this.filteredPayments = [...this.payments];
    } else if (this.paidChecked && !this.voidChecked) {
      this.filteredPayments = [...this.payments.filter((x) => x.paymentStatus === 'PAID')];
    } else if (!this.paidChecked && this.voidChecked) {
      this.filteredPayments = [...this.payments.filter((x) => x.paymentStatus === 'VOIDED')];
    } else {
      this.filteredPayments = [];
    }
    this.calculateTotal();
  }

  updateFilter(evt: {startDate: Date; endDate: Date; txStatus?: string; datePreset?: string}) {
    this.router.navigate(['/payment-list'], {
      queryParams: {
        datePreset: evt.datePreset ?? this.datePreset,
        startDate: evt.startDate ?? this.startDate,
        endDate: evt.endDate ?? this.endDate,
        txStatus: evt.txStatus ?? this.txStatus,
      },
    });
  }

  changeDate(evt: {startDate: Date; endDate: Date; datePreset: string}) {

    this.updateFilter({
      startDate: evt.startDate,
      endDate: evt.endDate,
      datePreset: evt.datePreset,
    });
  }

  changeTxStatus(evt: string) {
    this.txStatusEnum = this.convertTxStatusToEnum(evt);
    this.updateFilter({
      datePreset: this.datePreset,
      startDate: new Date(this.startDate),
      endDate: new Date(this.endDate),
      txStatus: evt,
    });
  }

  private convertTxStatusToEnum(evt: string): PaymentStatusEnum {
    switch (evt) {
      case 'ALL':
        return null;
      case 'PAID':
        return PaymentStatusEnum.Paid;
      case 'VOIDED':
        return PaymentStatusEnum.Voided;
      default:
        return null;
    }
  }

  protected readonly PaymentStatusEnum = PaymentStatusEnum;
}
